import React, { useEffect, useState } from 'react'
import { loginUserState } from '@store/loginUser'
import { ReactNode } from 'react'
import { createContext, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { defaultLoginValue } from '@store/loginUser'

import {
  getAccessTokenFromStorage,
  removeTokenFromStorage,
  setAccessTokenToStorage,
} from '@utils/storage'
import { api } from '@api/index'

export interface ContextValues {
  user: LoginUser | null | undefined
  login: (data: LoginUser) => void
  logout: () => void
}

const contextDefaultValue = {
  user: undefined,
  login: () => {},
  logout: () => {},
}

export const AuthContext = createContext<ContextValues>(contextDefaultValue)

interface Props {
  children: ReactNode
}

export const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useRecoilState(loginUserState)
  const loc = useLocation()

  const navigate = useNavigate()

  const login = async (data: LoginUser) => {
    setUser(data)
    setAccessTokenToStorage(data.token)
    navigate('/home')
  }

  const logout = () => {
    setUser(null)
    removeTokenFromStorage()
    navigate('/', { replace: true })
  }

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
    }),
    [user]
  )

  /**
   * 사용자가 새로고침시 토큰 검증
   * @author jeongyun
   */
  useEffect(() => {
    const token = getAccessTokenFromStorage()
    //1.access 토큰이 없는 경우 -> force logout
    if (!token) {
      setUser(null)
      return
    }
    //2.access 토큰이 있는 경우 -> fetching login
    const validateToken = async () => {
      const user = await api.userService.validateToken()
      if (user) {
        //2.1 리프레시 토큰 검증 성공 -> setUser(user)
        const token = getAccessTokenFromStorage()
        setUser({ ...user, token })
        if (loc.pathname === '/') {
          navigate('/home', { replace: true })
        }
      } else {
        //2.2 리프레시 토큰 검증 실패 -> setUser(null)
        setUser(null)
      }
    }
    validateToken()
  }, [])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
